import { PropsWithChildren, useRef, useCallback } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import {
  makeStyles,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Button,
  Grid,
  Divider,
  Typography,
} from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import PermMediaIcon from '@material-ui/icons/PermMedia';
import CloseIcon from '@material-ui/icons/Close';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  SearchBar,
  SearchResult,
  SearchResultPager,
  useSearch,
} from '@backstage/plugin-search-react';
import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';
import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';
import { CatalogIcon, DocsIcon } from '@backstage/core-components';
import { useNavigate } from 'react-router-dom';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: '100%',
  },
});

const CustomSearchModalContent = ({
  toggleModal,
}: {
  toggleModal: () => void;
}) => {
  const navigate = useNavigate();
  const { term } = useSearch();
  const searchBarRef = useRef<HTMLInputElement>(null);

  const handleSubmit = useCallback(() => {
    const query = searchBarRef.current?.value ?? '';
    navigate(`/search?query=${query}`);
    toggleModal();
  }, [navigate, toggleModal]);

  const hasQuery = term && term.trim().length > 0;

  return (
    <>
      <DialogTitle>
        <Box
          display="grid"
          gridTemplateColumns="1fr auto"
          alignItems="center"
          style={{ gap: 8 }}
        >
          <SearchBar
            inputProps={{ ref: searchBarRef }}
            onSubmit={handleSubmit}
          />
          <IconButton aria-label="close" onClick={toggleModal}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid
          container
          direction="row-reverse"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Grid item>
            <Button
              color="primary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleSubmit}
              disableRipple
            >
              View full results
            </Button>
          </Grid>
        </Grid>
        <Divider />
        {hasQuery ? (
          <SearchResult>
            <CatalogSearchResultListItem icon={<CatalogIcon />} />
            <TechDocsSearchResultListItem icon={<DocsIcon />} />
          </SearchResult>
        ) : (
          <Box pt={4} pb={4} textAlign="center">
            <Typography variant="body1" color="textSecondary">
              Start typing to search across the catalog and documentation.
            </Typography>
          </Box>
        )}
      </DialogContent>
      {hasQuery && (
        <DialogActions style={{ padding: '8px 24px' }}>
          <Grid container direction="row">
            <Grid item xs={12}>
              <SearchResultPager />
            </Grid>
          </Grid>
        </DialogActions>
      )}
    </>
  );
};

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <Link to="/" underline="none" className={classes.link} aria-label="Home">
      {isOpen ? <LogoFull /> : <LogoIcon />}
    </Link>
  );
};

const LogoutButton = () => {
  const identityApi = useApi(identityApiRef);
  return (
    <SidebarItem
      icon={ExitToAppIcon}
      text="Logout"
      onClick={() => identityApi.signOut()}
    />
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <section className="htec-bs-sidebar">
      <Sidebar>
        <SidebarLogo />
        <SidebarDivider style={{ background: 'none' }} />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Menu items in Figma order */}
          <SidebarGroup
            className="htec-sidebar-search"
            label="Search"
            icon={<SearchIcon />}
            to="/search"
          >
            <SidebarSearchModal>
              {({ toggleModal }) => (
                <CustomSearchModalContent toggleModal={toggleModal} />
              )}
            </SidebarSearchModal>
          </SidebarGroup>
          <SidebarItem
            className="htec-sidebar-home"
            icon={HomeIcon}
            to="/"
            text="Home"
          />
          <SidebarItem icon={PermMediaIcon} to="catalog" text="Accelerators" />
          {/* <SidebarItem icon={LibraryBooks} to="docs" text="Doc" /> */}
          {/* <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." /> */}
          <SidebarScrollWrapper>
            {/* Items in this group will be scrollable if they run out of space */}
          </SidebarScrollWrapper>
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider style={{ background: 'none' }} />
        <SidebarGroup label="Settings">
          <NotificationsSidebarItem />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            <SidebarSettings />
          </SidebarGroup>
          <LogoutButton />
        </SidebarGroup>
      </Sidebar>
    </section>
    {children}
  </SidebarPage>
);
